import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "mb-3" }
const _hoisted_2 = { class: "form-floating mb-3" }
const _hoisted_3 = { class: "form-floating mb-3" }
const _hoisted_4 = { class: "form-floating mb-3" }
const _hoisted_5 = { class: "d-grid gap-2 d-md-flex justify-content-md-end mt-4" }

import { PropType, ref, computed, provide, watch } from "vue";
import { PensionPot } from "@/guiide/ts/pensionPot";
import NumberInput from "../common/NumberInput.vue";
import BadResponseViewer from "../../../shared/components/BadResponseViewer.vue";
import LockButton from "@/guiide/components/common/LockButton.vue";
import ObjectBuilder from "@/guiide/ts/objectBuilder";
import Constants from "@/guiide/ts/constants";
import { app } from "@/guiide/ts/app";


export default /*@__PURE__*/_defineComponent({
  __name: 'PensionPotEditor',
  props: {
    pensionPot: { type: Object as PropType<PensionPot>, required: true },
},
  emits: ["edit-finished"],
  setup(__props, { emit: __emit }) {

const emit = __emit;

const props = __props;

const currentValueModel = ref<number | null>(0);
const personalContributionModel = ref<number | null>(0);
const employerContributionModel = ref<number | null>(0);
const currentValueInput = ref(null);
const personalMonthlyContributionInput = ref(null);
const employerMonthlyContributionInput = ref(null);
const badResponseViewer = ref(null);
const execute = ref(false);
provide('execute', execute);

watch(() => props.pensionPot, (newPensionPot) => {
  if (isNew(newPensionPot)) {
    currentValueModel.value = null;
    personalContributionModel.value = null;
    employerContributionModel.value = null;
  } else {
    currentValueModel.value = newPensionPot.valuationAmount;
    personalContributionModel.value = newPensionPot.personalMonthlyContribution;
    employerContributionModel.value = newPensionPot.employerMonthlyContribution;
  }
});

const buttonText = computed<string>(() => {
  return props.pensionPot.id === Constants.NewId ? "Add" : "Update";
});

const isInViewOnlyMode = computed<boolean>(() => {
  return !(props.pensionPot.userCanEditCurrentValue || props.pensionPot.userCanEditContribution);
});

const cancelButtonText = computed<string>(() => {
  return isInViewOnlyMode.value ? "Close" : "Cancel";
});

async function onSaveShortcut() {
  execute.value = true;
}

async function onSaveClicked() {
  if (!isValid()) {
    execute.value = false;
    return;
  }

  const willMakeFutureContribs = personalContributionModel.value > 0 || employerContributionModel.value > 0;

  const update = ObjectBuilder.createPensionPot(
    props.pensionPot.id,
    props.pensionPot.name,
    currentValueModel.value,
    0,
    0,
    0,
    willMakeFutureContribs,
    personalContributionModel.value,
    employerContributionModel.value,
    true,
    props.pensionPot.userCanEditCurrentValue,
    props.pensionPot.userCanEditContribution
  );

  const responseTracker = await app.updatePensionPot(update);

  execute.value = false;

  if (responseTracker.isOk) {
    editIsFinished();
  } else {
    if (responseTracker.trackerWillHandleFailure) {
      responseTracker.handleFailure();
    } else {
      badResponseViewer.value.update(
        responseTracker.isOk,
        responseTracker.message
      );
    }
  }
};

function isValid(): boolean {
  if (!currentValueInput.value.isValid()) {
    return false;
  }

  if (!personalMonthlyContributionInput.value.isValid()) {
    return false;
  }

  if (!employerMonthlyContributionInput.value.isValid()) {
    return false;
  }

  return true;
};

function editIsFinished() {
  currentValueInput.value.hideAnyWarnings();
  personalMonthlyContributionInput.value.hideAnyWarnings();
  employerMonthlyContributionInput.value.hideAnyWarnings();
  badResponseViewer.value.clear();
  emit("edit-finished");
};

function isNew(pensionPot: PensionPot): boolean {
  return pensionPot.id === Constants.NewId;
}

function onCancelClicked() {
  currentValueModel.value = props.pensionPot.valuationAmount;
  personalContributionModel.value = props.pensionPot.personalMonthlyContribution;
  employerContributionModel.value = props.pensionPot.employerMonthlyContribution;
  editIsFinished();
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("h5", _hoisted_1, _toDisplayString(props.pensionPot.name), 1),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(NumberInput, {
        ref_key: "currentValueInput",
        ref: currentValueInput,
        modelValue: currentValueModel.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((currentValueModel).value = $event)),
        description: "value",
        "is-read-only": !props.pensionPot.userCanEditCurrentValue,
        max: 10000000,
        onEntered: onSaveShortcut
      }, null, 8, ["modelValue", "is-read-only"])
    ]),
    _cache[3] || (_cache[3] = _createElementVNode("p", null, " How much personal contribution is being paid in each month? ", -1)),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(NumberInput, {
        ref_key: "personalMonthlyContributionInput",
        ref: personalMonthlyContributionInput,
        modelValue: personalContributionModel.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((personalContributionModel).value = $event)),
        description: "personal monthly contribution",
        "is-read-only": !props.pensionPot.userCanEditContribution,
        optional: true,
        min: 0,
        max: 20000,
        onEntered: onSaveShortcut
      }, null, 8, ["modelValue", "is-read-only"])
    ]),
    _cache[4] || (_cache[4] = _createElementVNode("p", null, " How much employer contribution being paid in each month? ", -1)),
    _createElementVNode("div", _hoisted_4, [
      _createVNode(NumberInput, {
        ref_key: "employerMonthlyContributionInput",
        ref: employerMonthlyContributionInput,
        modelValue: employerContributionModel.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((employerContributionModel).value = $event)),
        description: "employer monthly contribution",
        "is-read-only": !props.pensionPot.userCanEditContribution,
        optional: true,
        min: 0,
        max: 20000,
        onEntered: onSaveShortcut
      }, null, 8, ["modelValue", "is-read-only"])
    ]),
    _cache[5] || (_cache[5] = _createElementVNode("p", null, "We will assume these contribution amounts rise each year as your salary increases.", -1)),
    _createVNode(BadResponseViewer, {
      ref_key: "badResponseViewer",
      ref: badResponseViewer
    }, null, 512),
    _createElementVNode("div", _hoisted_5, [
      (!isInViewOnlyMode.value)
        ? (_openBlock(), _createBlock(LockButton, {
            key: 0,
            text: buttonText.value,
            "async-action": onSaveClicked
          }, null, 8, ["text"]))
        : _createCommentVNode("", true),
      _createElementVNode("button", {
        type: "button",
        class: "btn btn-secondary me-md-2",
        "aria-label": "Close",
        onClick: onCancelClicked
      }, _toDisplayString(cancelButtonText.value), 1)
    ])
  ], 64))
}
}

})